// Client-side Sogni access: cookie-SSO auth (shared .sogni.ai session with
// app.sogni.ai, same pattern as sogni-chat) and renders billed to the
// logged-in user's Unlimited entitlement or token balance.

import { SogniClient } from './vendor/sogni-client.js';
import { isUnlimitedSubscription } from './account.js';

export const APP_SOURCE = 'sogni-infinity';
const APP_ID_KEY = 'sogni-infinity-app-id';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function persistentAppId(storage = localStorage, randomUUID = () => crypto.randomUUID()) {
  let id = storage.getItem(APP_ID_KEY);
  if (!UUID_PATTERN.test(id || '')) {
    id = randomUUID();
    storage.setItem(APP_ID_KEY, id);
  }
  return id;
}

let clientPromise = null;

export function getClient() {
  if (!clientPromise) {
    clientPromise = SogniClient.createInstance({
      appId: persistentAppId(),
      appSource: APP_SOURCE,
      network: 'fast',
      restEndpoint: 'https://api.sogni.ai',
      socketEndpoint: 'wss://socket.sogni.ai',
      // Model-availability updates must stay ON: chat.waitForModels() is fed
      // by the swarmLLMModels socket event, so opting out to save traffic
      // made custom subjects fail with "Timeout waiting for LLM models".
      socketEventSubscriptions: { modelAvailability: true },
      multiInstance: true,
      authType: 'cookies'
    });
  }
  return clientPromise;
}

/**
 * The sampler / scheduler / VAE a model actually accepts, straight from the
 * platform: `{ sampler: {allowed, default}, scheduler: {…}, vae?: {…} }`.
 *
 * The allowed lists are model-specific, so the settings UI asks per model
 * rather than hard-coding a menu that would drift the moment a tier changes.
 */
export async function modelOptions(modelId) {
  const client = await getClient();
  return client.projects.getModelOptions(modelId);
}

export async function checkAuth() {
  const client = await getClient();
  try {
    const account = client.account.currentAccount;
    // SDK typo is real: isAuthenicated
    if (account?.isAuthenicated) return { authed: true, username: account.username };
    const ok = await client.checkAuth();
    return { authed: !!ok, username: client.account.currentAccount?.username };
  } catch {
    return { authed: false, username: null };
  }
}

export async function loginAccount({ username, password, remember }) {
  const client = await getClient();
  const result = await client.account.login(username, password, remember, APP_SOURCE);
  if (remember) localStorage.setItem('sogni-persist', 'true');
  else localStorage.removeItem('sogni-persist');
  return result;
}

export async function validateUsername(username) {
  const client = await getClient();
  return client.account.validateUsername(username);
}

export async function createAccount({
  username,
  email,
  password,
  subscribe,
  referralCode,
  remember,
  turnstileToken
}) {
  const client = await getClient();
  const result = await client.account.create(
    {
      username,
      email,
      password,
      subscribe,
      referralCode: referralCode || undefined,
      turnstileToken,
      appSource: APP_SOURCE
    },
    remember
  );
  if (remember) localStorage.setItem('sogni-persist', 'true');
  else localStorage.removeItem('sogni-persist');
  return result;
}

// ---------- payment method ----------
// Which pot a render is billed to, chosen by the visitor and applied to every
// project. Three methods, matching sogni-web and the socket's payment model:
//   plan  → billingMode 'auto',  tokenType 'spark' — an active Unlimited plan
//           covers the render (coverage requires plan billing + spark); without
//           a plan this simply spends Spark.
//   spark → billingMode 'tokens', tokenType 'spark' — always spend Spark.
//   sogni → billingMode 'tokens', tokenType 'sogni' — always spend SOGNI.
const PAY_KEY = 'sogni-infinity-payment';
const PAY_METHODS = new Set(['plan', 'spark', 'sogni']);

export function paymentMethod() {
  try {
    const m = localStorage.getItem(PAY_KEY);
    return PAY_METHODS.has(m) ? m : 'plan';
  } catch {
    return 'plan';
  }
}

export function setPaymentMethod(method) {
  if (!PAY_METHODS.has(method)) return;
  try {
    localStorage.setItem(PAY_KEY, method);
  } catch {
    /* private mode: the choice just does not persist across reloads */
  }
}

/** The token the current (or given) method spends. */
export function paymentToken(method = paymentMethod()) {
  return method === 'sogni' ? 'sogni' : 'spark';
}

/** The project.create billing params for the chosen method. */
export function billingParams(method = paymentMethod()) {
  return method === 'plan'
    ? { billingMode: 'auto', tokenType: 'spark' }
    : { billingMode: 'tokens', tokenType: paymentToken(method) };
}

/**
 * Whether the latest billing snapshot proves that a render cannot be paid
 * for. Unknown balances deliberately return false so a temporary balance API
 * failure does not prevent the renderer from giving the authoritative answer.
 */
export function hasKnownEmptyBalance(billing, method = paymentMethod()) {
  if (method === 'plan' && billing?.unlimited) return false;
  const balance = method === 'sogni' ? billing?.sogni : billing?.spark;
  return balance != null && Number(balance) <= 0;
}

export async function accountBilling() {
  const client = await getClient();
  const [subscriptionResult, balanceResult] = await Promise.allSettled([
    client.account.refreshSubscription(),
    client.account.refreshBalance()
  ]);
  const subscription =
    subscriptionResult.status === 'fulfilled'
      ? subscriptionResult.value
      : client.account.currentAccount?.subscription;
  const balances = balanceResult.status === 'fulfilled' ? balanceResult.value : null;
  const spark = balances?.spark?.net ?? balances?.spark?.settled;
  const sogni = balances?.sogni?.net ?? balances?.sogni?.settled;
  return {
    subscription,
    unlimited: isUnlimitedSubscription(subscription),
    spark: spark != null ? Math.floor(Number(spark)) : null,
    // SOGNI is not a whole-token count like Spark; keep two decimals so a
    // fractional balance is not floored to a misleading zero.
    sogni: sogni != null ? Math.round(Number(sogni) * 100) / 100 : null
  };
}

export async function accountRewards() {
  const client = await getClient();
  return client.account.rewards();
}

export async function claimAccountReward(reward, turnstileToken) {
  const client = await getClient();
  await client.account.claimRewards([reward.id], {
    turnstileToken,
    provider: reward.provider
  });
}

export async function logout() {
  const client = await getClient();
  try {
    await client.account.logout();
  } catch {
    /* session already gone */
  }
}

// The LLM normalizes a free-form idea into a subject phrase that obeys the
// research rules (material word, one colour family, compliant blobby shape).
// It deliberately does NOT write the tessellation clauses — those are proven
// and composed server-side, so the model cannot drop or reword them.
const SUBJECT_SYSTEM = `You turn a user's idea into a SUBJECT PHRASE for an Escher-style tessellation. A separate system chooses the drawing style, so describe WHAT is depicted, never how it is drawn.

Rules, all mandatory:
- Reply with ONLY the subject phrase. No quotes, no preamble, no trailing punctuation.
- Name ONE concrete thing with a strong, recognisable silhouette, singular, led by its article ("a"/"an").
- Prefer rounded or curled forms that can interlock: animals, faces, leaves, shells, waves, skulls.
- Add one or two words of distinguishing detail if it helps the shape read.
- Do NOT name a medium, style, material finish, or lighting — those are chosen elsewhere.
- Keep it under 8 words. No scenes, no text, no backgrounds.

Examples:
cats -> a curled sleeping cat
something spooky -> a human skull
dinosaurs -> a long-necked dinosaur
the ocean -> a cresting ocean wave
my girlfriend -> a woman with long flowing hair`;

export async function normalizeSubject(idea, allowMature = false) {
  const client = await getClient();
  // Prefer whatever the swarm advertises; fall back to any list already
  // cached on the client so a slow availability broadcast degrades into a
  // normal API error rather than a hard timeout.
  let models = {};
  try {
    models = await client.chat.waitForModels(15_000);
  } catch {
    models = client.chat.models || {};
  }
  const model = Object.keys(models)[0];
  if (!model) throw new Error('No LLM workers are available right now — try again shortly.');
  const result = await client.chat.completions.create({
    appSource: APP_SOURCE,
    model,
    messages: [
      { role: 'system', content: SUBJECT_SYSTEM },
      { role: 'user', content: idea }
    ],
    max_tokens: 200,
    temperature: 0.8,
    stream: false,
    // With the content filter off, declaring it lets sogni-socket route an
    // adult prompt to the abliterated model. The default model refuses such
    // prompts — correct while the filter is on, useless once the user has
    // turned it off. The backend owns both the NSFW test and the model swap
    // (jobsController.resolveLLMModelForRequest), so the keyword list is not
    // duplicated here where it would silently go stale.
    ...(allowMature ? { safeContentFilter: false } : {})
  });
  return (
    String(result?.content || '')
      .replace(/<think>[\s\S]*?<\/think>/gi, '') // reasoning models may prepend a think block
      .replace(/^["'\s]+|["'.\s]+$/g, '')
      .split('\n')
      .filter(Boolean)
      .pop() || ''
  );
}

// Codes that genuinely mean "this account cannot pay for the job", checked
// against the platform rather than guessed. From sogni-api
// error-handling/errorcodes.ts: INSUFFICIENT_BALANCE = 123 and
// LLM_INSUFFICIENT_BALANCE = 174; from its contract code table,
// INSUFFICIENT_SPARK_BALANCE = 14. sogni-web matches the same 123 in
// integrations/chains/etherlink.
//
// Deliberately NOT here: 4078. sogni-client's SUBSCRIPTION_ERROR_CODES names
// it NOT_ENTITLED — "no active subscription entitlement covers the job" — so
// treating it as an empty balance told a subscriber to go buy Spark for a
// problem buying Spark does not fix. (It should not reach us at all: renders
// go out with billingMode 'auto', not 'subscription'.) 4024 was in this set
// too and is not an error code anywhere in sogni-api, sogni-socket or
// sogni-client.
const INSUFFICIENT_BALANCE_CODES = new Set([123, 14, 174]);

export function isInsufficientFunds(err) {
  const code = Number(err?.payload?.errorCode ?? err?.errorCode ?? err?.code ?? err?.status);
  if (Number.isFinite(code) && INSUFFICIENT_BALANCE_CODES.has(code)) return true;
  const msg = String(err?.payload?.message || err?.message || err?.error || err || '');
  // "insufficient balance", "insufficient SPARK balance", "not enough spark".
  return /insufficient|not enough|balance too low|debit error/i.test(msg);
}

/**
 * Estimate the cost of one Tessellate roll (one batch) from the platform's own
 * estimator — never a hardcoded guess. Steps and guidance come from the model's
 * defaults, which is exactly what the render uses (renderBatch omits them), and
 * previewCount is 0 because the render requests no progressive previews. Returns
 * { spark, sogni, usd } as numbers, or null when an estimate is unavailable so
 * the caller can simply hide the readout.
 */
export async function estimateRollCost({
  model,
  imageCount,
  size,
  sampler,
  scheduler,
  steps,
  guidance: guidanceOverride,
  contextImages,
  tokenType = 'spark'
}) {
  const client = await getClient();
  if (!model || !imageCount || typeof client.projects?.estimateCost !== 'function') return null;

  // The render leaves steps/guidance to the model default, so read those and
  // estimate with them — otherwise the number would not match the real spend.
  // Photo mode is the exception: it sends explicit values, so the estimate
  // must use the same ones.
  const opts = await client.projects.getModelOptions(model);
  const stepCount = Number(steps ?? opts?.steps?.default);
  const guidance = Number(guidanceOverride ?? opts?.guidance?.default);

  const result = await client.projects.estimateCost({
    network: 'fast',
    tokenType,
    model,
    imageCount,
    stepCount: Number.isFinite(stepCount) ? stepCount : 0,
    previewCount: 0,
    width: size,
    height: size,
    ...(Number.isFinite(guidance) ? { guidance } : {}),
    ...(sampler ? { sampler } : {}),
    ...(scheduler ? { scheduler } : {}),
    ...(contextImages ? { contextImages } : {})
  });

  const num = (v) => {
    const n = typeof v === 'string' ? parseFloat(v) : Number(v);
    return Number.isFinite(n) ? n : null;
  };
  return { spark: num(result?.spark), sogni: num(result?.sogni), usd: num(result?.usd) };
}

/**
 * Render a whole batch as ONE project.
 *
 * Four separate projects meant four queue slots and four round trips for what
 * is one idea. Sogni's dynamic-prompt syntax — `{a|b|c}` — expands a single
 * prompt into N jobs server-side, so a batch that varies by style costs one
 * request. Jobs finish OUT OF ORDER, so results are reported per job as they
 * land rather than assumed to arrive in slot order.
 *
 * onTile(url, index) fires per completed job; the promise resolves when all do
 * AND every onTile handler has settled. Callers score tiles inside onTile, so
 * resolving before those async handlers finish made the caller's winner check
 * run against a partial score list and reroll past a passing tile.
 */
export async function renderBatch({
  model,
  prompts,
  seed,
  size,
  vae,
  sampler,
  scheduler,
  steps,
  guidance,
  contextImages,
  groundingPx,
  refBoost,
  refBoostA,
  loras,
  loraStrengths,
  outputFormat,
  allowMature = false,
  onTile,
  signal,
  timeoutMs = 300_000
}) {
  const client = await getClient();
  const unique = [...new Set(prompts)];
  // One prompt for the whole batch when every slot shares it; otherwise the
  // dynamic-prompt branch, which the server expands into one job per option.
  const positivePrompt = unique.length === 1 ? unique[0] : `{${prompts.join('|')}}`;
  const params = {
    type: 'image',
    appSource: APP_SOURCE,
    // Billed to the visitor's chosen method: an active Unlimited plan (auto),
    // Spark, or SOGNI. See billingParams / the payment method note above.
    ...billingParams(),
    modelId: model,
    numberOfMedia: prompts.length,
    positivePrompt,
    seed,
    sizePreset: 'custom',
    width: size,
    height: size,
    // Omitted rather than sent empty: the SDK fills in the model's own
    // defaults for anything absent, so an unset knob stays the platform
    // default instead of becoming an invalid explicit value.
    ...(vae ? { vae } : {}),
    ...(sampler ? { sampler } : {}),
    ...(scheduler ? { scheduler } : {}),
    // Photo mode sends these explicitly (the identity-edit model's published
    // defaults); everything else leaves them to the model like the knobs above.
    ...(steps ? { steps } : {}),
    ...(guidance != null ? { guidance } : {}),
    // The visitor's photo, as Blob(s) — uploaded by the SDK straight to the
    // renderer's presigned URL. It never touches the Infinity server.
    ...(contextImages?.length ? { contextImages } : {}),
    ...(loras?.length ? { loras, loraStrengths } : {}),
    ...(outputFormat ? { outputFormat } : {}),
    ...(allowMature ? { disableNSFWFilter: true } : {})
  };
  const identityFields = { groundingPx, refBoost, refBoostA };
  const fields = Object.entries(identityFields).filter(([, value]) => value != null);
  const socket = client.apiClient?.socket;
  if (!fields.length || !socket || typeof socket.send !== 'function') {
    return trackProject(await client.projects.create(params));
  }
  const originalSend = socket.send;
  const boundSend = originalSend.bind(socket);
  const patchedSend = (messageType, data) => {
    if (messageType === 'jobRequest' && Array.isArray(data?.keyFrames)) {
      for (const keyFrame of data.keyFrames) {
        if (keyFrame?.modelID !== model) continue;
        for (const [field, value] of fields) keyFrame[field] = value;
      }
    }
    return boundSend(messageType, data);
  };
  socket.send = patchedSend;
  let project;
  try {
    project = await client.projects.create(params);
  } finally {
    if (socket.send === patchedSend) socket.send = originalSend;
  }
  return trackProject(project);

  async function trackProject(project) {
    if (signal?.aborted) {
      await project.cancel().catch(() => {});
      throw new DOMException('Render stopped', 'AbortError');
    }

    let done = 0;
    const seen = new Set();
    const tileWork = [];
    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = (callback, value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        signal?.removeEventListener('abort', abort);
        callback(value);
      };
      // Both success paths funnel through here so resolution always waits for
      // the async onTile handlers (scoring round-trips) already in flight.
      const resolveAfterTileWork = () => {
        Promise.allSettled(tileWork).then(() => finish(resolve));
      };
      const abort = () => {
        project.cancel().catch(() => {});
        finish(reject, new DOMException('Render stopped', 'AbortError'));
      };
      const timer = setTimeout(() => finish(reject, new Error('render timeout')), timeoutMs);
      signal?.addEventListener('abort', abort, { once: true });
      project.on('jobCompleted', (job) => {
        // A job landing after settlement belongs to a batch the caller has
        // already moved past — reporting it would paint into a reset UI.
        if (settled || signal?.aborted || !job?.resultUrl || seen.has(job.id)) return;
        seen.add(job.id);
        const index = done++;
        try {
          tileWork.push(
            // The job/project ids ride along so a tile the visitor reports can
            // name the exact generation to the platform's feedback API — that
            // is what makes a report actionable rather than just a complaint.
            Promise.resolve(
              onTile?.(job.resultUrl, index, job.positivePrompt || positivePrompt, {
                jobId: job.id,
                projectId: project.id
              })
            )
          );
        } catch {
          /* UI only */
        }
        if (done >= prompts.length) {
          resolveAfterTileWork();
        }
      });
      project.on('failed', (e) => {
        finish(reject, new Error(e?.message || 'project failed'));
      });
      project
        .waitForCompletion()
        .then(resolveAfterTileWork)
        .catch((e) => finish(reject, e));
    });
  }
}

export async function renderTile({
  model,
  prompt,
  seed,
  size,
  vae,
  sampler,
  scheduler,
  loras,
  loraStrengths,
  outputFormat,
  allowMature = false,
  timeoutMs = 240_000
}) {
  const client = await getClient();
  const project = await client.projects.create({
    type: 'image',
    appSource: APP_SOURCE,
    ...billingParams(),
    modelId: model,
    numberOfMedia: 1,
    positivePrompt: prompt,
    seed,
    sizePreset: 'custom',
    width: size,
    height: size,
    ...(vae ? { vae } : {}),
    ...(sampler ? { sampler } : {}),
    ...(scheduler ? { scheduler } : {}),
    ...(loras?.length ? { loras, loraStrengths } : {}),
    ...(outputFormat ? { outputFormat } : {}),
    // Off by default; the user opts in explicitly via the app's toggle.
    ...(allowMature ? { disableNSFWFilter: true } : {})
  });
  const urls = await Promise.race([
    project.waitForCompletion(),
    new Promise((_, reject) => setTimeout(() => reject(new Error('render timeout')), timeoutMs))
  ]);
  if (!urls?.[0]) throw new Error('no result URL');
  return urls[0];
}

// ---------- tile animation (MiniMax H3 first/last-frame loop) ----------
//
// The tile is handed to H3's flf2v workflow as BOTH anchor frames, so the clip
// is pinned to the artwork at t=0 and t=end — a closed loop by construction.
// Feasibility was measured before shipping (research/video-loop.mjs): the
// loop-boundary frame diff sits below ordinary adjacent-frame motion, and the
// spatial wrap survives animation on the app's own per-frame seam validator.
//
// Two checkpoints, same price: the standard 20-step model is platform-gated to
// Unlimited / Premium Spark / SOGNI billing (error 4081); Turbo (4 steps) runs
// on plain Spark. The renderer prefers standard when the visitor's payment
// method can use it and falls back to Turbo on the gate error, so a Spark
// account is never dead-ended.

const H3_FLF2V = 'minimax-h3-fl2va-fp8_flf2v';
const H3_FLF2V_TURBO = 'minimax-h3-fl2va-fp8_flf2v_turbo';

/**
 * The engines a loop can render on. LTX 2.3 distilled is the default: its
 * 64px canvas grid runs past 1024, so the anchor is the tile at its NATIVE
 * resolution — no downscale — and it takes both frame anchors like H3 does.
 * The two H3 checkpoints render at the model's 768 tier (1024² is over its
 * 1,032,192-pixel cap). Frame counts sit on each model's own grid: LTX at
 * `1 + n*8` (121 ≈ 5.0s), H3 at `124 + n*17` (124 ≈ 5.17s), both 24fps.
 */
export const ANIMATION_ENGINES = {
  ltx: {
    label: 'LTX 2.3',
    model: 'ltx23-22b-fp8_i2v_distilled',
    size: 1024,
    frames: 121,
    seconds: 5,
    steps: 8
  },
  'h3-turbo': {
    label: 'MiniMax H3 Turbo',
    model: H3_FLF2V_TURBO,
    size: 768,
    frames: 124,
    seconds: 124 / 24,
    steps: 4
  },
  h3: {
    label: 'MiniMax H3',
    model: H3_FLF2V,
    size: 768,
    frames: 124,
    seconds: 124 / 24,
    steps: 20
  }
};

/**
 * Snap a requested duration onto an engine's frame grid at 24fps: LTX frames
 * follow `1 + n*8` inside 4-20s, H3 `124 + n*17` inside 124-362 frames. The
 * EFFECTIVE seconds come back with the frames because the H3 alignment line
 * must state the real end-mark, not the label the visitor clicked.
 */
export function animationSeconds(engine, requestedSeconds = 5) {
  const wanted = Number(requestedSeconds) || 5;
  if (engine === 'ltx') {
    const clamped = Math.max(4, Math.min(20, wanted));
    const frames = 1 + Math.round((clamped * 24) / 8) * 8;
    return { frames, seconds: (frames - 1) / 24 };
  }
  const raw = wanted * 24;
  const frames = Math.max(124, Math.min(362, 124 + Math.round((raw - 124) / 17) * 17));
  return { frames, seconds: frames / 24 };
}

/**
 * The motion sentence both prompts share. A visitor's own suggestion replaces
 * the generic description but keeps the stay-anchored / cyclical framing —
 * those clauses are what stop motion from breaking the spatial wrap, so they
 * are scaffold, not preference.
 */
function motionSentence(subjectLabel, motionHint) {
  const hint = String(motionHint || '').trim();
  if (hint) {
    const clean = hint.replace(/[.\s]+$/, '');
    return (
      `${clean}. Every element stays anchored in its own position in the pattern, ` +
      'and all motion is small, smooth, and cyclical.'
    );
  }
  const motif = String(subjectLabel || '').trim() || 'motif';
  return (
    `Every ${motif} stays anchored in its own position in the pattern while it breathes and ` +
    'sways gently in place, with small, smooth, cyclical movement.'
  );
}

/**
 * The H3 FL2VA loop prompt. The alignment first line is MiniMax's required
 * Context-IR contract, byte-for-byte (em dash, bare Picture/Shot, 2-decimal
 * duration). The camera-lock and stay-anchored language is load-bearing: any
 * pan or zoom, or a motif leaving its station, breaks the spatial wrap that
 * makes the result tile. The frame is described as a fixed crop of a larger
 * pattern so motion at an edge reads as continuation, not an exit.
 */
function h3AnimationPrompt(subjectLabel, seconds, motionHint) {
  return (
    'How the reference pictures align with the target video — ' +
    'Picture 1 (from Shot 1) aligns with the 0.00-second mark of the target video; ' +
    `Picture 2 (from Shot 1) aligns with the ${seconds.toFixed(2)}-second mark of the target video.\n\n` +
    'integrated_multimodal_description: [Shot 1] An animated ornamental pattern, one continuous ' +
    'shot. The frame shows exactly the repeating tessellation artwork of Picture 1 and is a fixed ' +
    'crop of a larger pattern that continues beyond every edge, so motion near each edge simply ' +
    'continues past it. The camera is completely locked: no pan, no zoom, no rotation, no ' +
    `parallax, and the artwork fills the frame at all times. ${motionSentence(subjectLabel, motionHint)} ` +
    'The linework, colors, palette, and composition remain identical to ' +
    'Picture 1 throughout, and all motion is small and cyclical so the arrangement settles back ' +
    'into the exact composition of Picture 2 at the end of the shot. Nothing new enters the ' +
    'frame, no element disappears, and no text, logos, or watermarks appear.\n\n' +
    'overall_soundscape: Soft, even ambience matching the artwork, with quiet continuous texture ' +
    'and no sudden events.\n\n' +
    'non_diegetic_music: N/A'
  );
}

/** The LTX loop prompt: ordinary prose (no Context-IR), carrying the same
 *  camera-lock / stay-anchored / return-to-start contract. */
function ltxAnimationPrompt(subjectLabel, motionHint) {
  return (
    'One continuous locked-off shot of an ornamental repeating tessellation pattern, animated. ' +
    'The camera never moves: no pan, no zoom, no rotation, no parallax; the flat artwork fills ' +
    `the frame edge to edge for the entire shot. ${motionSentence(subjectLabel, motionHint)} ` +
    'The linework, colors, palette, and composition stay identical to the ' +
    'reference artwork throughout, and all motion is small and cyclical, returning the pattern ' +
    'exactly to its opening arrangement at the end of the shot. The frame is a fixed crop of a ' +
    'larger pattern that continues beyond every edge, so motion near each edge simply continues ' +
    'past it. No new elements appear, nothing disappears, and no text, logos, or watermarks ' +
    'appear. Steady soft ambience; no music.'
  );
}

export async function estimateAnimationCost(
  tokenType = paymentToken(),
  engine = 'ltx',
  seconds = 5
) {
  const cfg = ANIMATION_ENGINES[engine] || ANIMATION_ENGINES.ltx;
  const client = await getClient();
  if (typeof client.projects?.estimateVideoCost !== 'function') return null;
  const result = await client.projects.estimateVideoCost({
    tokenType,
    model: cfg.model,
    width: cfg.size,
    height: cfg.size,
    frames: animationSeconds(engine, seconds).frames,
    fps: 24,
    steps: cfg.steps
  });
  const num = (v) => {
    const n = typeof v === 'string' ? parseFloat(v) : Number(v);
    return Number.isFinite(n) ? n : null;
  };
  return { spark: num(result?.spark), sogni: num(result?.sogni), usd: num(result?.usd) };
}

/** The platform's "this model wants a better plan" refusal (NOT_ENTITLED-style
 *  4081 for pro video), as distinct from an empty balance. */
function isVideoEntitlementError(err) {
  const code = Number(err?.code ?? err?.payload?.errorCode ?? err?.errorCode);
  if (code === 4081) return true;
  return /requires an Unlimited subscription/i.test(
    String(err?.payload?.message || err?.message || err?.limitation || '')
  );
}

/**
 * Animate a tile into a ~5s seamless loop. `image` must be the tile's own
 * image-model output (as a Blob at the engine's canvas size) — never a frame
 * extracted from a video.
 *
 * Resolves { url, model } with the mp4's remote URL. Both engines generate
 * their soundtrack jointly with the video and the prompt directs a steady
 * ambience, so the audio ships by default; the anchors close the loop
 * visually while the wall's mute button covers anyone who prefers silence.
 */
export async function renderTileAnimation({
  image,
  subjectLabel,
  engine = 'ltx',
  seconds = 5,
  motionHint,
  withAudio = true,
  seed,
  signal,
  onProgress,
  timeoutMs = 900_000
}) {
  const client = await getClient();

  const attempt = async (engineKey) => {
    const cfg = ANIMATION_ENGINES[engineKey] || ANIMATION_ENGINES.ltx;
    const effective = animationSeconds(engineKey, seconds);
    const project = await client.projects.create({
      type: 'video',
      appSource: APP_SOURCE,
      ...billingParams(),
      modelId: cfg.model,
      numberOfMedia: 1,
      positivePrompt:
        engineKey === 'ltx'
          ? ltxAnimationPrompt(subjectLabel, motionHint)
          : h3AnimationPrompt(subjectLabel, effective.seconds, motionHint),
      width: cfg.size,
      height: cfg.size,
      duration: effective.seconds,
      fps: 24,
      // H3 fixes its sampling server-side and the SDK rejects explicit steps
      // there; LTX distilled takes its 8 steps like sogni-web's flow sends.
      ...(engineKey === 'ltx' ? { steps: cfg.steps } : {}),
      seed,
      generateAudio: withAudio,
      referenceImage: image,
      referenceImageEnd: image
    });
    if (signal?.aborted) {
      await project.cancel().catch(() => {});
      throw new DOMException('Animation stopped', 'AbortError');
    }
    const abort = () => project.cancel().catch(() => {});
    const progress = (value) => {
      const percent = Number(value) <= 1 ? Number(value) * 100 : Number(value);
      if (Number.isFinite(percent)) onProgress?.(Math.max(0, Math.min(100, percent)));
    };
    signal?.addEventListener('abort', abort, { once: true });
    project.on('progress', progress);
    try {
      const urls = await Promise.race([
        project.waitForCompletion(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('animation timeout')), timeoutMs)
        )
      ]);
      if (signal?.aborted) throw new DOMException('Animation stopped', 'AbortError');
      if (!urls?.[0]) throw new Error('no result URL');
      return { url: urls[0], model: cfg.model };
    } finally {
      signal?.removeEventListener('abort', abort);
      project.off('progress', progress);
    }
  };

  if (engine !== 'h3-turbo') {
    try {
      return await attempt(engine);
    } catch (err) {
      if (signal?.aborted || !isVideoEntitlementError(err)) throw err;
      // The account's billing cannot use the chosen model after all — H3
      // Turbo renders a loop on plain Spark rather than dead-ending.
    }
  }
  return attempt('h3-turbo');
}

// ---------- content reports ----------

/**
 * File a report about one of the visitor's own generations with the platform.
 *
 * Renders here happen on the visitor's own Sogni account, so the content lives
 * in that account and only the platform can act on it — the report goes to the
 * same `/v1/feedback/reports` endpoint, with the same taxonomy and field names,
 * that sogni-web's report modal posts to (see its AppModals/ReportContent).
 * Tiles published to the Infinity wall are a separate path: that wall is hosted
 * by this app, so those reports go to its own /api/stream/:id/flag.
 *
 * `includeGeneration` is the visitor's choice to attach the prompt and the
 * image URL. Without it the report still carries the ids and model so the
 * platform can find the job, but nothing the visitor wrote is sent along.
 */
export async function submitContentReport({
  projectId,
  jobId,
  resultUrl,
  reason,
  note,
  includeGeneration = true,
  prompt,
  model,
  seed
}) {
  const client = await getClient();
  const feedback = String(note || '').trim() || reason;
  // The platform's coarse bucket. sogni-web maps every content category to
  // 'offensive' and keeps the fine-grained value in `reason`; matching that
  // exactly keeps both apps' reports in one queue shape.
  const reportReason = reason === 'broken_or_incorrect' ? 'broken_or_incorrect' : 'offensive';
  const body = {
    feedback,
    reportType: 'generation',
    reportReason,
    reason,
    sourceApp: APP_SOURCE,
    source: APP_SOURCE,
    platform: 'web',
    appVersion: APP_SOURCE,
    mediaType: 'image',
    mediaCount: 1,
    sessionId: projectId,
    messageId: jobId,
    projectId,
    jobId,
    ...(model ? { modelName: model } : {}),
    generationSubmitted: includeGeneration,
    generationMetadata: {
      mediaType: 'image',
      projectId,
      jobId,
      ...(model ? { modelName: model } : {}),
      ...(Number.isFinite(Number(seed)) ? { seed: Number(seed) } : {}),
      ...(includeGeneration && resultUrl ? { mediaUrl: resultUrl } : {}),
      ...(includeGeneration && prompt ? { prompt: { positivePrompt: prompt } } : {}),
      client: { source: APP_SOURCE, platform: 'web' }
    },
    ...(includeGeneration && resultUrl ? { resultUrl } : {})
  };
  const response = await client.apiClient.rest.post('/v1/feedback/reports', body);
  return response?.data || { ok: true };
}

// ---------- creative upscale ----------

// The upscale prompt is written fresh by a vision LLM from the pixels rather
// than reusing the render prompt: the guide image owns the structure, so the
// prompt's job is to name what is visible and push clean detail — the same
// division of labour as sogni-web's image prompt writer.
const UPSCALE_SYSTEM = `You are an expert prompt engineer for AI image generation. You write ONE positive prompt that recreates the attached image at higher resolution with richer, crisper detail.

Rules, all mandatory:
- Reply with ONLY the prompt text. No quotes, no preamble, no lists.
- Describe the visible subject, composition, colour palette, mood, and medium precisely.
- The image is one tile of a seamless repeating pattern. Describe it as an allover repeating pattern; never as a framed scene, and never mention borders, edges, or margins.
- Preserve the source identity, composition, shapes, colours, and seamless pattern structure, but do NOT preserve its softness.
- Inspect what is actually visible and name the precise fine-scale structures and material-specific microtextures that should be synthesized for those subjects and that medium. Do not mention details belonging to subjects or materials that are not visible.
- Command the model to fill the enlarged composition with dense, coherent, sharply resolved detail: crisp contours, clearly separated small features, rich surface definition, and sharp local contrast throughout. Use the visible content to decide what meaningful new detail fills the added image capacity.
- Specify clean ISO 64 clarity and controlled low-noise tonal transitions.
- Keep reconstructed texture coherent. Do not substitute grain, speckling, rough noise, indiscriminate smoothing, smeared detail, painterly fuzz, soft focus, bloom, or exaggerated sharpening for real detail.
- Keep it under 120 words.`;

export async function describeTileForUpscale({ dataUrl, sourcePrompt, allowMature = false }) {
  const client = await getClient();
  let models = {};
  try {
    models = await client.chat.waitForModels(15_000);
  } catch {
    models = client.chat.models || {};
  }
  const model = Object.keys(models)[0];
  if (!model) throw new Error('No LLM workers are available right now — try again shortly.');
  const text = [
    'Write the positive prompt for a 2x creative upscale of this tile.',
    sourcePrompt
      ? `The tile was rendered from this prompt — reuse its intent and vocabulary where it matches what you see: ${sourcePrompt}`
      : ''
  ]
    .filter(Boolean)
    .join('\n\n');
  const result = await client.chat.completions.create({
    appSource: APP_SOURCE,
    model,
    messages: [
      { role: 'system', content: UPSCALE_SYSTEM },
      {
        role: 'user',
        content: [
          { type: 'image_url', image_url: { url: dataUrl, detail: 'auto' } },
          { type: 'text', text }
        ]
      }
    ],
    max_tokens: 600,
    temperature: 0.9,
    stream: false,
    ...(allowMature ? { safeContentFilter: false } : {})
  });
  return String(result?.content || '')
    .replace(/<think>[\s\S]*?<\/think>/gi, '')
    .replace(/^["'\s]+|["\s]+$/g, '')
    .trim();
}

/**
 * One img2img render paints detail back in at double resolution. Strength 0.70
 * leaves exactly three reconstruction steps at Krea's ten-step default while
 * keeping the source as a strong structural guide. Guidance, sampler and
 * scheduler stay on the model's own defaults.
 */
export async function renderUpscale({
  model,
  vae,
  prompt,
  image,
  strength = 0.7,
  seed,
  size,
  negativePrompt,
  outputFormat,
  allowMature = false,
  signal,
  onProgress,
  timeoutMs = 300_000
}) {
  const client = await getClient();
  const project = await client.projects.create({
    type: 'image',
    appSource: APP_SOURCE,
    ...billingParams(),
    modelId: model,
    numberOfMedia: 1,
    positivePrompt: prompt,
    ...(negativePrompt ? { negativePrompt } : {}),
    seed,
    sizePreset: 'custom',
    width: size,
    height: size,
    startingImage: image,
    startingImageStrength: strength,
    ...(vae ? { vae } : {}),
    ...(outputFormat ? { outputFormat } : {}),
    ...(allowMature ? { disableNSFWFilter: true } : {})
  });
  if (signal?.aborted) {
    await project.cancel().catch(() => {});
    throw new DOMException('Upscale stopped', 'AbortError');
  }
  const abort = () => project.cancel().catch(() => {});
  const progress = (percent) => {
    if (Number.isFinite(percent)) onProgress?.(Math.max(0, Math.min(100, percent)));
  };
  signal?.addEventListener('abort', abort, { once: true });
  project.on('progress', progress);
  try {
    const urls = await Promise.race([
      project.waitForCompletion(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('upscale timeout')), timeoutMs))
    ]);
    if (signal?.aborted) throw new DOMException('Upscale stopped', 'AbortError');
    if (!urls?.[0]) throw new Error('no result URL');
    return urls[0];
  } finally {
    signal?.removeEventListener('abort', abort);
    project.off('progress', progress);
  }
}
